import React, { useState, useEffect } from 'react';
import {
  TrendingUp, TrendingDown, DollarSign, MousePointerClick,
  Target, AlertTriangle, Lightbulb, Play, Pause, Settings,
  BarChart3, RefreshCw, ChevronDown, ChevronUp, Zap, X
} from 'lucide-react';
import { getGoogleAdsClient, getDateRange, GoogleAdsClient } from '@/lib/google-ads-client';
import { createGoogleAdsAIOptimizer } from '@/lib/google-ads-ai-optimizer';
import { supabase } from '@/lib/supabase';
import toast from 'react-hot-toast';

interface Campaign {
  id: string;
  name: string;
  status: string;
  impressions: number;
  clicks: number;
  cost: number;
  conversions: number;
  ctr: number;
  avgCpc: number;
  roas: number;
  aiScore?: number;
  aiRecommendations?: string[];
}

export default function GoogleAdsCampaignManagerPage() {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [aiReport, setAiReport] = useState<any>(null);
  const [dateRange, setDateRange] = useState(30);
  const [expandedCampaigns, setExpandedCampaigns] = useState<Set<string>>(new Set());
  const [showFullReport, setShowFullReport] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<'hales' | 'myccs' | 'abraham'>('hales');

  const companyOptions = [
    { value: 'hales', label: "Hale's AC" },
    { value: 'myccs', label: 'Climate Control (myccs)' },
    { value: 'abraham', label: 'Abraham A/C' },
  ];

  useEffect(() => {
    loadCampaigns();
  }, [dateRange, selectedCompany]);

  const loadCampaigns = async () => {
    try {
      setLoading(true);

      // Get customer ID from database
      const { data: mappings } = await supabase
        .from('google_ads_customer_mapping')
        .select('*')
        .eq('company', selectedCompany)
        .eq('is_active', true)
        .limit(1)
        .maybeSingle();

      if (!mappings) {
        toast.error(`No Google Ads account connected for ${selectedCompany}`);
        return;
      }

      const customerId = mappings.customer_id;
      const client = getGoogleAdsClient(customerId);
      const range = getDateRange(dateRange);

      // Fetch campaign data
      const [campaignList, metrics] = await Promise.all([
        client.listCampaigns(customerId, { status: 'ENABLED', company: selectedCompany }),
        client.getCampaignMetrics(customerId, [], range, selectedCompany),
      ]);

      // Aggregate metrics by campaign
      const campaignMap = new Map<string, Campaign>();
      
      for (const campaign of campaignList) {
        campaignMap.set(campaign.id, {
          id: campaign.id,
          name: campaign.name,
          status: campaign.status,
          impressions: 0,
          clicks: 0,
          cost: 0,
          conversions: 0,
          ctr: 0,
          avgCpc: 0,
          roas: 0,
        });
      }

      // Aggregate metrics
      for (const metric of metrics) {
        const campaign = campaignMap.get(metric.campaignId);
        if (campaign) {
          campaign.impressions += metric.impressions;
          campaign.clicks += metric.clicks;
          campaign.cost += metric.costMicros / 1000000;
          campaign.conversions += metric.conversions;
        }
      }

      // Calculate derived metrics
      const campaignsArray = Array.from(campaignMap.values()).map(c => ({
        ...c,
        ctr: c.impressions > 0 ? c.clicks / c.impressions : 0,
        avgCpc: c.clicks > 0 ? c.cost / c.clicks : 0,
        roas: c.cost > 0 ? (c.conversions * 100) / c.cost : 0, // Simplified ROAS calculation
      }));

      setCampaigns(campaignsArray);

      // Generate AI recommendations
      await generateAIRecommendations(client, customerId, range);

    } catch (error) {
      console.error('Error loading campaigns:', error);
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const generateAIRecommendations = async (
    client: GoogleAdsClient,
    customerId: string,
    range: { startDate: string; endDate: string }
  ) => {
    try {
      const optimizer = createGoogleAdsAIOptimizer(client);
      const report = await optimizer.generateOptimizationReport(customerId, range);
      setAiReport(report);

      // Update campaigns with AI scores
      setCampaigns(prev => prev.map(c => {
        const bidRecs = report.bidRecommendations.filter(
          b => campaigns.find(camp => camp.id === c.id)
        );
        return {
          ...c,
          aiScore: report.overallScore,
          aiRecommendations: bidRecs.slice(0, 3).map(b => b.reason),
        };
      }));

    } catch (error) {
      console.error('Error generating AI recommendations:', error);
    }
  };

  const handlePauseCampaign = async (campaignId: string) => {
    try {
      const { data: mappings } = await supabase
        .from('google_ads_customer_mapping')
        .select('customer_id')
        .eq('company', selectedCompany)
        .eq('is_active', true)
        .limit(1)
        .maybeSingle();

      if (!mappings) return;

      const client = getGoogleAdsClient(mappings.customer_id);
      await client.pauseCampaign(mappings.customer_id, campaignId, selectedCompany);
      
      toast.success('Campaign paused successfully');
      loadCampaigns();
    } catch (error) {
      console.error('Error pausing campaign:', error);
      toast.error('Failed to pause campaign');
    }
  };

  const handleResumeCampaign = async (campaignId: string) => {
    try {
      const { data: mappings } = await supabase
        .from('google_ads_customer_mapping')
        .select('customer_id')
        .eq('company', selectedCompany)
        .eq('is_active', true)
        .limit(1)
        .maybeSingle();

      if (!mappings) return;

      const client = getGoogleAdsClient(mappings.customer_id);
      await client.resumeCampaign(mappings.customer_id, campaignId, selectedCompany);
      
      toast.success('Campaign resumed successfully');
      loadCampaigns();
    } catch (error) {
      console.error('Error resuming campaign:', error);
      toast.error('Failed to resume campaign');
    }
  };

  const toggleCampaignExpanded = (campaignId: string) => {
    setExpandedCampaigns(prev => {
      const next = new Set(prev);
      if (next.has(campaignId)) {
        next.delete(campaignId);
      } else {
        next.add(campaignId);
      }
      return next;
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatPercent = (value: number) => {
    return `${(value * 100).toFixed(2)}%`;
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
          <span className="ml-3 text-lg">Loading campaigns...</span>
        </div>
      </div>
    );
  }

  const totalStats = campaigns.reduce((acc, c) => ({
    impressions: acc.impressions + c.impressions,
    clicks: acc.clicks + c.clicks,
    cost: acc.cost + c.cost,
    conversions: acc.conversions + c.conversions,
  }), { impressions: 0, clicks: 0, cost: 0, conversions: 0 });

  return (
    <div className="p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Campaign Manager</h1>
            <p className="text-gray-600 mt-1">AI-powered Google Ads campaign management</p>
          </div>
          <div className="flex gap-3">
            <select
              value={selectedCompany}
              onChange={(e) => setSelectedCompany(e.target.value as 'hales' | 'myccs' | 'abraham')}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              {companyOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(Number(e.target.value))}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
            <button
              onClick={loadCampaigns}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </button>
          </div>
        </div>

        {/* AI Score Banner */}
        {aiReport && (
          <div className="bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <Zap className="h-6 w-6" />
                  <span className="text-2xl font-bold">AI Optimization Score: {aiReport.overallScore}/100</span>
                </div>
                <p className="text-sm opacity-90">
                  {aiReport.bidRecommendations.length} bid recommendations • 
                  {aiReport.negativeKeywords.length} negative keywords • 
                  {aiReport.campaignHealthIssues.length} health issues
                </p>
              </div>
              <button
                onClick={() => setShowFullReport(true)}
                className="px-6 py-3 bg-white text-purple-600 rounded-lg font-semibold hover:bg-gray-100"
              >
                View Full Report
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-600 text-sm">Total Spend</span>
            <DollarSign className="h-5 w-5 text-red-500" />
          </div>
          <div className="text-2xl font-bold text-gray-900">
            {formatCurrency(totalStats.cost)}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-600 text-sm">Total Clicks</span>
            <MousePointerClick className="h-5 w-5 text-blue-500" />
          </div>
          <div className="text-2xl font-bold text-gray-900">
            {totalStats.clicks.toLocaleString()}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-600 text-sm">Conversions</span>
            <Target className="h-5 w-5 text-green-500" />
          </div>
          <div className="text-2xl font-bold text-gray-900">
            {totalStats.conversions.toLocaleString()}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-600 text-sm">Avg CTR</span>
            <TrendingUp className="h-5 w-5 text-purple-500" />
          </div>
          <div className="text-2xl font-bold text-gray-900">
            {totalStats.impressions > 0 
              ? formatPercent(totalStats.clicks / totalStats.impressions)
              : '0%'
            }
          </div>
        </div>
      </div>

      {/* Campaigns List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Active Campaigns ({campaigns.length})
          </h2>
        </div>

        <div className="divide-y divide-gray-200">
          {campaigns.map((campaign) => {
            const isExpanded = expandedCampaigns.has(campaign.id);
            
            return (
              <div key={campaign.id} className="p-6 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between">
                  {/* Campaign Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <button
                        onClick={() => toggleCampaignExpanded(campaign.id)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        {isExpanded ? (
                          <ChevronUp className="h-5 w-5" />
                        ) : (
                          <ChevronDown className="h-5 w-5" />
                        )}
                      </button>
                      <h3 className="text-lg font-semibold text-gray-900">{campaign.name}</h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        campaign.status === 'ENABLED' 
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {campaign.status}
                      </span>
                    </div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 ml-8">
                      <div>
                        <div className="text-xs text-gray-500">Impressions</div>
                        <div className="text-sm font-semibold">{campaign.impressions.toLocaleString()}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Clicks</div>
                        <div className="text-sm font-semibold">{campaign.clicks.toLocaleString()}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Cost</div>
                        <div className="text-sm font-semibold">{formatCurrency(campaign.cost)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Conversions</div>
                        <div className="text-sm font-semibold">{campaign.conversions}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">CTR</div>
                        <div className="text-sm font-semibold">{formatPercent(campaign.ctr)}</div>
                      </div>
                    </div>

                    {/* AI Recommendations (when expanded) */}
                    {isExpanded && campaign.aiRecommendations && campaign.aiRecommendations.length > 0 && (
                      <div className="mt-4 ml-8 p-4 bg-purple-50 rounded-lg border border-purple-200">
                        <div className="flex items-center gap-2 mb-3">
                          <Lightbulb className="h-5 w-5 text-purple-600" />
                          <span className="font-semibold text-purple-900">AI Recommendations</span>
                        </div>
                        <ul className="space-y-2">
                          {campaign.aiRecommendations.map((rec, idx) => (
                            <li key={idx} className="text-sm text-purple-800 flex items-start gap-2">
                              <span className="text-purple-400 mt-1">•</span>
                              <span>{rec}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    {campaign.status === 'ENABLED' ? (
                      <button
                        onClick={() => handlePauseCampaign(campaign.id)}
                        className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                        title="Pause Campaign"
                      >
                        <Pause className="h-5 w-5" />
                      </button>
                    ) : (
                      <button
                        onClick={() => handleResumeCampaign(campaign.id)}
                        className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                        title="Resume Campaign"
                      >
                        <Play className="h-5 w-5" />
                      </button>
                    )}
                    <button
                      onClick={() => setSelectedCampaign(campaign)}
                      className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Settings"
                    >
                      <Settings className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                {/* Performance Indicator */}
                {isExpanded && (
                  <div className="mt-4 ml-8">
                    <div className="flex items-center gap-4">
                      {campaign.conversions > 0 ? (
                        <div className="flex items-center gap-2 text-green-600">
                          <TrendingUp className="h-5 w-5" />
                          <span className="text-sm font-medium">Converting</span>
                        </div>
                      ) : campaign.clicks > 20 ? (
                        <div className="flex items-center gap-2 text-orange-600">
                          <AlertTriangle className="h-5 w-5" />
                          <span className="text-sm font-medium">Needs Optimization</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-gray-600">
                          <BarChart3 className="h-5 w-5" />
                          <span className="text-sm font-medium">Gathering Data</span>
                        </div>
                      )}
                      
                      {campaign.cost > 0 && (
                        <>
                          <span className="text-gray-300">|</span>
                          <span className="text-sm text-gray-600">
                            CPA: {campaign.conversions > 0 
                              ? formatCurrency(campaign.cost / campaign.conversions)
                              : 'N/A'
                            }
                          </span>
                          <span className="text-gray-300">|</span>
                          <span className="text-sm text-gray-600">
                            Avg CPC: {formatCurrency(campaign.avgCpc)}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {campaigns.length === 0 && (
          <div className="p-12 text-center text-gray-500">
            <BarChart3 className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p>No active campaigns found</p>
          </div>
        )}
      </div>

      {/* AI Insights Panel */}
      {aiReport && aiReport.insights && aiReport.insights.length > 0 && (
        <div className="mt-8 bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-2 mb-4">
            <Lightbulb className="h-6 w-6 text-yellow-500" />
            <h2 className="text-xl font-semibold text-gray-900">AI Strategic Insights</h2>
          </div>
          <div className="space-y-3">
            {aiReport.insights.map((insight: string, idx: number) => (
              <div key={idx} className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                <span className="text-blue-600 font-bold">{idx + 1}.</span>
                <span className="text-gray-700">{insight}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Full AI Report Modal */}
      {showFullReport && aiReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Zap className="h-6 w-6 text-purple-600" />
                <h2 className="text-2xl font-bold text-gray-900">Complete AI Optimization Report</h2>
              </div>
              <button
                onClick={() => setShowFullReport(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-8">
              {/* Overall Score Section */}
              <div className="bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg p-6 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Overall Optimization Score</h3>
                    <span className="text-4xl font-bold">{aiReport.overallScore}/100</span>
                  </div>
                  <div className="text-right">
                    <p className="text-sm opacity-90">
                      {aiReport.bidRecommendations?.length || 0} bid recommendations
                    </p>
                    <p className="text-sm opacity-90">
                      {aiReport.negativeKeywords?.length || 0} negative keywords
                    </p>
                    <p className="text-sm opacity-90">
                      {aiReport.campaignHealthIssues?.length || 0} health issues
                    </p>
                  </div>
                </div>
              </div>

              {/* Bid Recommendations */}
              {aiReport.bidRecommendations && aiReport.bidRecommendations.length > 0 && (
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Target className="h-5 w-5 text-blue-600" />
                    Bid Optimization Recommendations
                  </h3>
                  <div className="grid gap-4">
                    {aiReport.bidRecommendations.map((rec: any, idx: number) => (
                      <div key={idx} className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-semibold text-blue-900 mb-2">{rec.campaignName || `Recommendation ${idx + 1}`}</h4>
                            <p className="text-blue-800">{rec.reason}</p>
                            {rec.currentBid && rec.recommendedBid && (
                              <div className="mt-3 flex gap-4 text-sm">
                                <span className="text-gray-600">
                                  Current: {formatCurrency(rec.currentBid / 1000000)}
                                </span>
                                <span className="text-green-600 font-semibold">
                                  Recommended: {formatCurrency(rec.recommendedBid / 1000000)}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="text-blue-600 font-semibold">
                            Priority: {rec.priority || 'Medium'}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Negative Keywords */}
              {aiReport.negativeKeywords && aiReport.negativeKeywords.length > 0 && (
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-red-600" />
                    Recommended Negative Keywords
                  </h3>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
                      {aiReport.negativeKeywords.map((keyword: string, idx: number) => (
                        <div key={idx} className="bg-white px-3 py-2 rounded border text-red-800 text-center">
                          "{keyword}"
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Campaign Health Issues */}
              {aiReport.campaignHealthIssues && aiReport.campaignHealthIssues.length > 0 && (
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-orange-600" />
                    Campaign Health Issues
                  </h3>
                  <div className="space-y-3">
                    {aiReport.campaignHealthIssues.map((issue: any, idx: number) => (
                      <div key={idx} className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <span className="text-orange-600 font-bold">{idx + 1}.</span>
                          <div>
                            <h4 className="font-semibold text-orange-900">{issue.campaignName || 'Unknown Campaign'}</h4>
                            <p className="text-orange-800">{issue.description}</p>
                            <div className="mt-2 text-sm text-orange-700">
                              Severity: <span className="font-semibold">{issue.severity || 'Medium'}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Strategic Insights */}
              {aiReport.insights && aiReport.insights.length > 0 && (
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Lightbulb className="h-5 w-5 text-yellow-600" />
                    Strategic Insights & Recommendations
                  </h3>
                  <div className="space-y-3">
                    {aiReport.insights.map((insight: string, idx: number) => (
                      <div key={idx} className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <span className="text-yellow-600 font-bold">{idx + 1}.</span>
                          <span className="text-yellow-800">{insight}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Performance Trends */}
              {aiReport.performanceTrends && aiReport.performanceTrends.length > 0 && (
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-green-600" />
                    Performance Trends
                  </h3>
                  <div className="grid gap-3 md:grid-cols-2">
                    {aiReport.performanceTrends.map((trend: string, idx: number) => (
                      <div key={idx} className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <span className="text-green-600 font-bold">{idx + 1}.</span>
                          <span className="text-green-800">{trend}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Items */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  Recommended Actions
                </h3>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mt-1">1</span>
                    <div>
                      <h4 className="font-semibold text-gray-900">Review Bid Recommendations</h4>
                      <p className="text-gray-700">Implement AI-suggested bid adjustments to improve ROI and reduce wasted spend.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mt-1">2</span>
                    <div>
                      <h4 className="font-semibold text-gray-900">Add Negative Keywords</h4>
                      <p className="text-gray-700">Block irrelevant search terms to improve click quality and reduce costs.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="bg-orange-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mt-1">3</span>
                    <div>
                      <h4 className="font-semibold text-gray-900">Address Campaign Health Issues</h4>
                      <p className="text-gray-700">Fix policy violations and optimization opportunities to improve account performance.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mt-1">4</span>
                    <div>
                      <h4 className="font-semibold text-gray-900">Monitor Performance Weekly</h4>
                      <p className="text-gray-700">Review metrics regularly and implement new AI recommendations as they become available.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
